import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import FeaturesBar from "@/components/feature/FeaturesBar";
import CartDrawer from "@/components/feature/CartDrawer";
import CategoryHero from "@/pages/home/components/CategoryHero";
import SidebarFilters from "@/pages/home/components/SidebarFilters";
import ProductListing from "@/pages/home/components/ProductListing";
import { categories as mockCategories, colors as mockColors, materials as mockMaterials, products as mockProducts, type Product } from "@/mocks/products";
import { useCart } from "@/context/CartContext";
import {
  fetchNuvemshopCategories,
  fetchBestSellers,
  fetchCatalogCached,
  isNuvemshopEnabled,
  type NuvemshopCategory,
} from "@/services/nuvemshop";
import type { FilterOption } from "@/pages/home/components/SidebarFilters";
import { defaultVariant, defaultVariantLabel, variantImage, variantMaxQuantity } from "@/utils/variants";
import { collectionLabels, dailyPicks, homeOrder, newestProducts, normalizeText, parseCollection, PROMO_COUNT, storeDay } from "@/utils/collections";

const PAGE_SIZE = 12;
const ALL_CATEGORY_ID = "todos";

export default function Home() {
  const { addItem, openCart } = useCart();

  const [products, setProducts] = useState<Product[]>(isNuvemshopEnabled() ? [] : mockProducts);
  const [categories, setCategories] = useState<FilterOption[]>(
    isNuvemshopEnabled()
      ? [{ id: ALL_CATEGORY_ID, label: "Todos os produtos" }]
      : mockCategories.map((c) => ({ id: c.id, label: c.label }))
  );

  const [catalogError, setCatalogError] = useState("");
  const [loadingCatalog, setLoadingCatalog] = useState(isNuvemshopEnabled());
  const loadCatalog = useCallback((attempt = 0) => {
    setCatalogError("");
    setLoadingCatalog(true);
    // Independentes: os produtos aparecem assim que chegam, e uma falha nas
    // categorias (só um filtro) não derruba a vitrine.
    fetchCatalogCached()
      .then((realProducts: Product[]) => {
        // Produto sem nenhuma variação em estoque não é listado (a página
        // dele, aberta por link direto, continua mostrando "indisponível").
        setProducts(realProducts.filter((product) => product.inStock !== false));
        setLoadingCatalog(false);
      })
      .catch(() => {
        // Falhas passageiras (ex.: limite de chamadas da API) costumam passar
        // sozinhas: tenta de novo uma vez, em silêncio, antes de mostrar erro.
        if (attempt === 0) {
          window.setTimeout(() => loadCatalog(1), 1500);
          return;
        }
        setCatalogError("Não foi possível carregar os produtos. Tente novamente em alguns instantes.");
        setLoadingCatalog(false);
      });
    fetchNuvemshopCategories()
      .then((realCategories: NuvemshopCategory[]) =>
        setCategories([{ id: ALL_CATEGORY_ID, label: "Todos os produtos" }, ...realCategories])
      )
      .catch(() => { /* fica só "Todos os produtos" */ });
  }, []);
  useEffect(() => {
    if (isNuvemshopEnabled()) loadCatalog();
  }, [loadCatalog]);

  // Uma seção de filtro para cada atributo real que existir nos produtos
  // (Cor, Pedra, Tamanho, o que o lojista cadastrar na Nuvemshop), em vez de
  // uma lista fixa de "Cor"/"Material".
  const attributeFilters: Record<string, FilterOption[]> = useMemo(() => {
    if (!isNuvemshopEnabled()) {
      return {
        Cor: mockColors.map((c) => ({ id: c.id, label: c.label })),
        Material: mockMaterials.map((m) => ({ id: m.id, label: m.label })),
      };
    }
    const valuesByAttribute: Record<string, Set<string>> = {};
    for (const product of products) {
      for (const [attrName, values] of Object.entries(product.attributes ?? {})) {
        if (!valuesByAttribute[attrName]) valuesByAttribute[attrName] = new Set();
        values.forEach((value) => valuesByAttribute[attrName].add(value));
      }
    }
    return Object.fromEntries(
      Object.entries(valuesByAttribute).map(([attrName, values]) => [
        attrName,
        Array.from(values).map((value) => ({ id: value, label: value })),
      ])
    );
  }, [products]);

  const getProductAttributeValues = (product: Product, attrName: string): string[] => {
    if (!isNuvemshopEnabled()) {
      if (attrName === "Cor") return product.color ? [product.color] : [];
      if (attrName === "Material") return product.material ? [product.material] : [];
    }
    return product.attributes?.[attrName] ?? [];
  };

  // Categoria e coleção vêm da URL (menu do cabeçalho): /?categoria=ID,
  // /?colecao=novidades (20 mais novos) ou /?colecao=promocoes (25 do dia).
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultCategory = isNuvemshopEnabled() ? ALL_CATEGORY_ID : "colares";
  const collection = parseCollection(searchParams.get("colecao"));
  const searchQuery = (searchParams.get("q") ?? "").trim().slice(0, 80);
  const selectedCategory = collection || searchQuery ? "" : searchParams.get("categoria") ?? defaultCategory;
  const setSelectedCategory = (id: string) =>
    setSearchParams(id === defaultCategory ? {} : { categoria: id });

  // Mais vendidos: vem da consulta de mais vendidos da loja (a mesma das sugestões).
  const [bestSellers, setBestSellers] = useState<Product[] | null>(null);
  useEffect(() => {
    if (collection !== "vendidos" || bestSellers) return;
    let active = true;
    fetchBestSellers().then((items) => { if (active) setBestSellers(items); }).catch(() => { if (active) setBestSellers([]); });
    return () => { active = false; };
  }, [collection, bestSellers]);
  const [selectedPrices, setSelectedPrices] = useState<string[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string[]>>({});
  const [sortId, setSortId] = useState("relevancia");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [toast, setToast] = useState<string | null>(null);

  const toggleValue = (list: string[], value: string, setter: (next: string[]) => void) => {
    setter(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
    setVisibleCount(PAGE_SIZE);
  };

  const toggleAttribute = (attributeName: string, valueId: string) => {
    setSelectedAttributes((prev) => {
      const current = prev[attributeName] ?? [];
      const next = current.includes(valueId)
        ? current.filter((item) => item !== valueId)
        : [...current, valueId];
      return { ...prev, [attributeName]: next };
    });
    setVisibleCount(PAGE_SIZE);
  };

  // Ao trocar de categoria/coleção pelo menu: volta a ordem padrão (a da própria
  // coleção) e leva o visitante até a lista de produtos.
  const firstRender = useRef(true);
  useEffect(() => {
    setSortId("relevancia");
    setVisibleCount(PAGE_SIZE);
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    document.getElementById("catalogo")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [collection, selectedCategory, searchQuery]);

  const activeCategory = categories.find((category) => category.id === selectedCategory);
  const title = searchQuery ? `BUSCA: ${searchQuery.toUpperCase()}` : collection ? collectionLabels[collection].toUpperCase() : activeCategory ? activeCategory.label.toUpperCase() : "PRODUTOS";

  // Novidades: os 20 mais novos. Promoções: 25 sorteados, e o sorteio muda todo
  // dia (mesma seleção para todos no mesmo dia). Só entram produtos à venda.
  const day = storeDay();
  const baseProducts = useMemo(() => {
    if (collection === "novidades") return newestProducts(products);
    if (collection === "promocoes") return dailyPicks(products, PROMO_COUNT, day);
    if (collection === "vendidos") return bestSellers ?? [];
    // Vitrine: o Kit Empreendedor fixo no topo e o resto embaralhado a cada dia.
    return homeOrder(products, day);
  }, [products, bestSellers, collection, day]);

  const filtered = useMemo(() => {
    const categoryOk = (product: Product) => {
      if (collection || searchQuery) return true;
      if (!isNuvemshopEnabled()) return selectedCategory === "todos" || selectedCategory === "colares";
      if (selectedCategory === ALL_CATEGORY_ID) return true;
      return (product.categoryIds ?? []).includes(Number(selectedCategory));
    };

    const priceOk = (product: Product) => {
      if (selectedPrices.length === 0) return true;
      return selectedPrices.some((id) => {
        if (id === "p1") return product.price <= 50;
        if (id === "p2") return product.price > 50 && product.price <= 100;
        if (id === "p3") return product.price > 100 && product.price <= 200;
        if (id === "p4") return product.price > 200 && product.price <= 300;
        if (id === "p5") return product.price > 300;
        return true;
      });
    };

    const attributesOk = (product: Product) =>
      Object.entries(selectedAttributes).every(([attrName, selected]) => {
        if (selected.length === 0) return true;
        const values = getProductAttributeValues(product, attrName);
        return selected.some((value) => values.includes(value));
      });

    const needle = normalizeText(searchQuery);
    const searchOk = (product: Product) => !needle || normalizeText(product.name).includes(needle);
    return baseProducts.filter(
      (product) => categoryOk(product) && searchOk(product) && priceOk(product) && attributesOk(product)
    );
  }, [baseProducts, collection, searchQuery, selectedCategory, selectedPrices, selectedAttributes]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sortId === "menor") list.sort((a, b) => a.price - b.price);
    if (sortId === "maior") list.sort((a, b) => b.price - a.price);
    if (sortId === "novidades") list.sort((a, b) => b.id - a.id);
    if (sortId === "vendidos") list.sort((a, b) => a.id - b.id);
    return list;
  }, [filtered, sortId]);

  const pageItems = sorted.slice(0, visibleCount);
  const hasMore = visibleCount < sorted.length;

  const hasActiveFilters =
    selectedPrices.length > 0 || Object.values(selectedAttributes).some((list) => list.length > 0);

  const clearFilters = () => {
    setSelectedPrices([]);
    setSelectedAttributes({});
    setVisibleCount(PAGE_SIZE);
  };

  const handleAddToCart = (product: Product) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: variantImage(product, defaultVariant(product)),
      variantId: product.variantId,
      variantLabel: defaultVariantLabel(product),
      maxQuantity: variantMaxQuantity(defaultVariant(product)),
    });
    openCart();
    setToast(`${product.name} adicionado à sacola`);
    window.setTimeout(() => setToast(null), 2600);
  };

  return (
    <div className="storefront flex min-h-screen flex-col bg-background-50">
      <Header />
      <CategoryHero />

      <main id="catalogo" className="mx-auto w-full max-w-none scroll-mt-32 px-4 pb-16 pt-10 md:px-6">
        <div className="flex flex-col gap-10 lg:flex-row lg:gap-12">
          <SidebarFilters
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={(id) => {
              setSelectedCategory(id);
              setVisibleCount(PAGE_SIZE);
            }}
            selectedPrices={selectedPrices}
            onPriceToggle={(id) => toggleValue(selectedPrices, id, setSelectedPrices)}
            attributeFilters={attributeFilters}
            selectedAttributes={selectedAttributes}
            onAttributeToggle={toggleAttribute}
            loading={loadingCatalog}
          />

          <ProductListing
            title={title}
            totalCount={filtered.length}
            products={pageItems}
            sortId={sortId}
            onSortChange={setSortId}
            hasMore={hasMore}
            onLoadMore={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
            onAddToCart={handleAddToCart}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            loading={loadingCatalog}
            error={catalogError}
            onRetry={() => loadCatalog()}
          />
        </div>
      </main>

      <FeaturesBar />
      <Footer />
      <CartDrawer />

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-40 flex w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 items-center gap-3 rounded-lg bg-foreground-950 px-4 py-3 text-sm text-background-50 shadow-lg">
          <span className="flex h-5 w-5 items-center justify-center text-primary-400">
            <i className="ri-checkbox-circle-line text-lg"></i>
          </span>
          {toast}
        </div>
      )}
    </div>
  );
}


